import {Box, Card, Container, Typography} from "@mui/material";
import Grid from '@mui/material/Grid2';
import {useState} from "react";
import Pjdb1ImputEx from "../../../../components/inputs/Pjdb1ImputEx";
import Pjdb1Button from "../../../../components/inputs/Pjdb1Button";
import {workAttrList} from "../../types/workForm";
import useMutationHelper from "../../hooks/useMutationHelper";
import ClassInfoGroup from "./groups/ClassInfoGroup";
import ModelInputGroup, {ModelFormInputs} from "./groups/ModelInputGroup";
import InstanceInputGroup, {InstanceFormInputs} from "./groups/InstanceInputGroup";
import useRegisterInstance from "../../hooks/useRegisterInstance";
import useRegisterModel from "../../hooks/useRegisterModel";
import {ValidationError} from "../../utils/formUtil";
import {policies} from "../../policies";
import Forbidden from "../errors/Forbidden";
import {WorkClassDocument} from "../../../../types/pj1dbApiWork";

export const registerInstanceAndModelFormInitInputs: RegisterInstanceAndModelFormInputs = {
  [workAttrList.modelFile.key]: null,
}

export interface RegisterInstanceAndModelFormInputs extends InstanceFormInputs, ModelFormInputs {
}

export interface RegisterInstanceAndModelFormProps {
  work: WorkClassDocument;
  inputs: RegisterInstanceAndModelFormInputs
  setInputs: React.Dispatch<React.SetStateAction<RegisterInstanceAndModelFormInputs>>;
  // eslint-disable-next-line react/require-default-props
  validate?: () => boolean;
  // eslint-disable-next-line react/require-default-props
  onClear?: () => void;
  // eslint-disable-next-line react/require-default-props
  onError?: (err: unknown) => void;
  // eslint-disable-next-line react/require-default-props
  onSuccess?: () => void;
}

export function canRegisterInstanceAndModel() {
  return policies.registerInstance.can() && policies.registerModel.can();
}

/**
 * ワークのとインスタンス、モデルの登録を行うフォーム
 * @param props
 * @constructor
 */
export default function RegisterInstanceAndModelForm(props: RegisterInstanceAndModelFormProps) {
  const {
    updateWork,
    setModelFileErr,
    modelFileErr,
    ConfirmDialog,
    formRef,
    clear,
    isClear,
    isLoading,
  } = useEditFormHelper(props);

  if (!canRegisterInstanceAndModel()) {
    return <Forbidden/>
  }

  return (
    <>
      <ConfirmDialog
        title='ワーク情報登録'
        message='ワーク情報を登録します。入力内容に間違いがないか確認してください。'
        confirmButton='登録'
        confirmButtonStyle="primary"
      />

      <Container maxWidth="lg">
        <Card sx={{p: 3}}>
          <Grid container spacing={3} alignItems="flex-end" justifyContent='space-between' sx={{mb: 2}}>
            <Grid>
              <Typography variant="h6">対象物登録</Typography>
            </Grid>
            <Grid><Pjdb1ImputEx/></Grid>
          </Grid>

          <form ref={formRef}>
            <ClassInfoGroup work={props.work}/>

            <InstanceInputGroup inputs={props.inputs} setInputs={props.setInputs} isClear={isClear}/>

            <ModelInputGroup inputs={props.inputs} setInputs={props.setInputs} modelFileErr={modelFileErr}
                             setModelFileErr={setModelFileErr} isClear={isClear}/>
          </form>
        </Card>
      </Container>

      <Box component="section" sx={{textAlign: "center"}}>
        <Pjdb1Button
          label="クリア"
          variant="contained"
          color="inherit"
          isLoading={isLoading}
          sx={{m: 2}}
          onClick={clear}
        />
        <Pjdb1Button
          label="ワーク情報を登録"
          variant="contained"
          color="primary"
          isLoading={isLoading}
          sx={{m: 2}}
          onClick={() => {
            updateWork().catch(console.error)
          }}
        />
      </Box>
    </>
  )
}

function useEditFormHelper(params: RegisterInstanceAndModelFormProps) {
  const [modelFileErr, setModelFileErr] = useState({err: false, errMsg: ""});
  const {makeMutation, ...helper} = useMutationHelper({
    onClear: () => {
      params.setInputs({
        ...registerInstanceAndModelFormInitInputs,
        ...params.work.attr,
      });
      params.onClear?.();
    }
  });
  const {validate: validateInstance, mutate: registerInstance} = useRegisterInstance();
  const {validate: validateModel, mutate: registerModel} = useRegisterModel();

  const updateWork = makeMutation({
    onCompleted: () => {
      if (params.onSuccess) {
        params.onSuccess();
      } else {
        helper.notification.savedMsg();
      }
    },
    onError: (err) => {
      helper.handleError({error: err, message: '更新処理が失敗しました。'});
      params.onError?.(err);
    },
    validate: () => {
      if (!(params.validate?.() ?? true)) {
        return false;
      }

      const errors: ValidationError[] = [];

      const instanceRes = validateInstance(params.inputs);
      if (!instanceRes.isValid) {
        errors.push(...instanceRes.errors);
      }

      const modelRes = validateModel(params.inputs);
      if (!modelRes.isValid) {
        errors.push(...modelRes.errors);
        if (modelRes.errors.some((error) => error.field === workAttrList.modelFile.key)) {
          setModelFileErr({
            err: true,
            errMsg: modelRes.errors.find((error) => error.field === workAttrList.modelFile.key)?.message ?? ""
          });
        } else {
          setModelFileErr({err: false, errMsg: ""});
        }

        return false;
      }
      setModelFileErr({err: false, errMsg: ""});

      if (errors.length > 0) {
        helper.notification.errorMsg(errors.map((error) => error.message).join('\n'));
        return false;
      }

      return true;
    },
    callback: async () => {
      const instanceRes = await registerInstance({
        inputs: params.inputs,
        classId: params.work.attr[workAttrList.classId.key],
      });
      if (!instanceRes.isSuccess) {
        return helper.handleError({error: instanceRes.error, message: '更新処理が失敗しました。'});
      }

      const modelRes = await registerModel({
        inputs: params.inputs,
        instanceId: instanceRes.data,
      });
      if (!modelRes.isSuccess) {
        return helper.handleError({error: modelRes.error, message: modelRes.message});
      }

      return true;
    },
  });

  return {
    updateWork,
    modelFileErr,
    setModelFileErr,
    ...helper,
  }
}
